import type { ApiErrorBody } from './types'

const BASE = '/api'

/** An error carrying the backend's structured payload. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string | null
  readonly context: Record<string, unknown>

  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.detail ?? `Request failed (${status})`)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code ?? null
    this.context = body.context ?? {}
  }
}

function readCookie(name: string): string {
  const match = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : ''
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptions {
  method?: Method
  body?: unknown
  file?: File
  query?: Record<string, unknown>
  signal?: AbortSignal
}

function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      // Repeated keys: ?cuisine=Italian&cuisine=Mexican
      for (const item of value) params.append(key, String(item))
    } else {
      params.append(key, String(value))
    }
  }
  const serialised = params.toString()
  return serialised ? `?${serialised}` : ''
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, file, query, signal } = options
  const headers: Record<string, string> = {}

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }
  if (method !== 'GET') {
    const csrf = readCookie('mp_csrf')
    if (csrf) headers['X-CSRF-Token'] = csrf
  }

  // The browser sets the multipart boundary itself, so no Content-Type here.
  const form = file ? new FormData() : undefined
  if (form && file) form.append('file', file)

  const response = await fetch(`${BASE}${path}${buildQuery(query)}`, {
    method,
    headers,
    credentials: 'same-origin',
    ...(form ? { body: form } : {}),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  })

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  // Errors are always JSON; a few endpoints answer with plain text instead.
  const isJson = response.headers.get('content-type')?.includes('json') ?? false
  if (response.ok && !isJson) {
    return text as T
  }
  const payload: unknown = text ? JSON.parse(text) : null

  if (!response.ok) {
    throw new ApiError(response.status, (payload ?? {}) as Partial<ApiErrorBody>)
  }
  return payload as T
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>, signal?: AbortSignal) =>
    request<T>(path, { query, ...(signal ? { signal } : {}) }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, file: File) => request<T>(path, { method: 'POST', file }),
}